import type { Dictionary } from './en.ts';

export const ca: Dictionary = {
  common: {
    tagline: 'traductor de veu en temps real',
    interfaceLanguage: 'Idioma de la interfície',
  },

  login: {
    title: 'Entrada',
    lede: "Introduïu el codi d'accés a la demo. Aquest navegador el recordarà durant 12 hores.",
    submit: 'Entrar',
    checking: 'Comprovant…',
    wrong: 'El codi no és correcte. Reviseu-lo i torneu-ho a provar.',
    failed: "No s'ha pogut entrar. Torneu-ho a provar d'aquí a un moment.",
  },

  home: {
    title: 'Una conversa en dues llengües',
    lede: "Cadascú parla la seva llengua i sent l'altra traduïda. Dues persones per sala, sense registre.",
    myLanguage: 'La meva llengua',
    peerLanguage: 'La seva llengua',
    accessCode: "Codi d'accés a la demo",
    signOut: 'Sortir',
    samePair: 'Trieu dues llengües diferents — altrament no hi ha res a traduir.',
    create: 'Crea una sala',
    creating: 'S’està creant…',
    createFailed: 'No s’ha pogut crear la sala',

    readyTitle: 'La sala és a punt',
    youHear: (language: string) => `Sentiu: ${language}.`,
    theyHear: (language: string) => `L'altra persona sent: ${language}.`,
    inviteLabel: "Enllaç d'invitació — envieu-lo a l'altra persona",
    copy: 'Copia',
    copied: 'Copiat',
    inviteNote:
      "L'enllaç és vàlid 15 minuts i només hi deixa entrar una persona. No el publiqueu enlloc — és la clau de la sala.",
    sourceSubtitles: 'Mostra també les paraules originals',
    sourceSubtitlesOn:
      'Veuràs què ha dit realment cada persona, en la seva llengua, al costat de la traducció. El proveïdor ho cobra a part, de manera que la trucada costa més.',
    sourceSubtitlesOff:
      'Només es mostra la traducció. No es transcriu res en la llengua original i no es cobra res de més.',
    sourceSubtitlesReady: 'També es mostraran les paraules originals.',
    goToCheck: 'Comprova el meu so',
  },

  voice: {
    you: 'Vós',
    peer: "L'altra persona",
    language: 'Llengua',
    provider: 'Locució',
    openaiNote: 'veu integrada, sona de seguida',
    elevenlabsNote: 'veu més neta, comença quan acaba la frase',
    noVoices: 'no hi ha veus per a aquesta llengua',
    voice: 'Veu',
    builtIn: "Veu integrada d'OpenAI",
    loading: 'Carregant veus…',
    failed: "No s'han pogut carregar les veus. Torneu-ho a provar més tard.",
    hintBuiltIn: 'Una sola veu per a totes les llengües, com abans.',
    hintLibrary: "Una veu de la biblioteca d'ElevenLabs.",
    groupVerified: 'Comprovades per a aquesta llengua',
    groupOther: 'Altres veus',
    accent: 'Aquesta veu no està comprovada per a aquesta llengua i pot sonar amb accent.',
    noSample: 'Aquesta veu no té mostra',
    listen: 'Escoltar',
    stop: 'Aturar',
    needVoice: 'Trieu una veu per a cada banda amb ElevenLabs o torneu-la a OpenAI.',
  },

  join: {
    checking: 'S’està comprovant la invitació…',
    title: 'Invitació a una conversa',
    youWillHear: (language: string) => `Sentireu: ${language}.`,
    theyHear: (language: string) => `L'altra persona sent: ${language}.`,
    connect: 'Uneix-t’hi',
    connecting: 'S’està entrant…',
    full: 'La sala és plena.',
    failedTitle: 'No s’hi ha pogut entrar',
    createYourOwn: 'Creeu la vostra pròpia sala',
    noInvite: 'En aquest enllaç no hi ha cap invitació. Torneu-la a demanar.',
    unavailable: 'Aquesta sala no és disponible',
    joinFailed: 'No s’ha pogut entrar a la sala',
  },

  check: {
    title: 'Comprovació del so',
    lede: "Poseu-vos auriculars: sense auriculars la traducció torna al vostre micròfon i l'altra persona sent eco.",
    microphone: 'Micròfon',
    defaultMic: 'Micròfon per defecte',
    level: 'Nivell — digueu alguna cosa',
    applied: (features: string) => `El navegador ha aplicat: ${features}.`,
    appliedNone: 'cap processament',
    echoCancellation: "cancel·lació d'eco",
    noiseSuppression: 'supressió de soroll',
    autoGain: 'guany automàtic',
    measureNote: 'Mesureu amb els auriculars posats.',
    testSound: 'Prova el so',
    testAgain: 'Torna a provar el so',
    start: 'Sona bé, comencem',
    starting: 'S’està connectant…',
    micDenied:
      "S'ha denegat l'accés al micròfon. Permeteu-lo a la configuració del navegador i torneu a carregar la pàgina.",
    micNotFound: 'No s’ha trobat cap micròfon. Connecteu-ne un i torneu a carregar la pàgina.',
    micFailed: 'No s’ha pogut obrir el micròfon.',
  },

  call: {
    title: 'Conversa',
    timeLeft: (time: string) => `· en queden ${time}`,
    waitingPeer: "S'espera l'altra persona. Envieu-li l'enllaç d'invitació.",
    autoplayBlocked: 'El navegador ha blocat la reproducció del so.',
    enableSound: 'Activa el so',
    lagging:
      'La traducció va endarrerida. Podeu reiniciar el traductor — el fragment a mitges es perdrà.',
    translatorStopped: (reason: string) =>
      `La traducció s'ha aturat: ${reason}. La conversa continua — reinicieu el traductor.`,
    translationVolume: 'Volum de la traducció',
    hearOriginal: 'Sentir la veu original',
    muteMic: 'Apaga el micròfon',
    unmuteMic: 'Encén el micròfon',
    restart: 'Reinicia la traducció',
    asleep:
      'Ningú no ha parlat durant un minut, així que la traducció s’ha posat en pausa per estalviar costos. Es reprendrà sola quan parli el teu interlocutor.',
    wake: 'Reprèn ara',
    leave: 'Surt',
    peerMuted: "L'altra persona ha apagat el micròfon",

    metricConnect: 'Engegada del traductor',
    metricFirst: 'Primera traducció',
    metricLag: 'Retard estimat',
    metricRtt: 'RTT cap al proveïdor',
    metricVoiceDelay: 'Retard de la locució',
    metricCost: 'Cost fins ara (estimació)',
    costBreakdown: (openai: string, voice: string, credits: number) =>
      `OpenAI ${openai} · ElevenLabs ${voice} · crèdits: ${credits}`,
    costNote: 'Preus públics configurats al servidor, no la factura dels proveïdors.',
    metricVoiceLead: "ElevenLabs davant d'OpenAI, mitjana",
    voiceLeadEarlier: (seconds: string) => `${seconds} s abans`,
    voiceLeadLater: (seconds: string) => `${seconds} s després`,
    iHear: (voice: string) => `Sento: ${voice}`,
    peerHears: (voice: string) => `L'altra persona sent: ${voice}`,
    voiceFellBack: 'OpenAI (ElevenLabs no ha respost)',

    endedTitle: 'La conversa s’ha acabat',
    endedNewRoom: 'Per a una conversa nova, creeu una sala nova.',
    newRoom: 'Sala nova',
    rateTitle: 'Valoreu la conversa',
  },

  closed: {
    'peer-left': "L'altra persona ha acabat la conversa.",
    'time-limit': 'S’ha arribat al límit de temps de la demo.',
    'peer-lost': "L'altra persona ha estat absent massa estona.",
    'server-shutdown': 'El servidor s’està reiniciant.',
    expired: 'La sala ha caducat.',
    unknown: 'S’ha tancat la connexió.',
    signalLost: 'S’ha perdut el contacte amb el servidor. Creeu una sala nova.',
  },

  status: {
    serverConnecting: 'Servidor: connectant',
    serverOpen: 'Servidor: connectat',
    serverReconnecting: 'Servidor: reconnectant',
    serverClosed: 'Servidor: desconnectat',

    peerWaiting: "L'altra persona: esperant",
    peerConnecting: "L'altra persona: connectant",
    peerConnected: "L'altra persona: connectada",
    peerReconnecting: "L'altra persona: recuperant-se",
    peerFailed: "L'altra persona: sense connexió",
    peerGone: "L'altra persona: fora",

    translatorIdle: 'Traducció per a mi: no iniciada',
    translatorConnecting: 'Traducció per a mi: iniciant',
    translatorReady: 'Traducció per a mi: en marxa',
    translatorReconnecting: 'Traducció per a mi: recuperant-se',
    translatorFailed: 'Traducció per a mi: ha fallat',
    translatorClosed: 'Traducció per a mi: aturada',
    translatorAsleep: 'Traducció per a mi: en pausa',

    peerTranslatorReady: "Traducció per a l'altra persona: en marxa",
    peerTranslatorWaiting: "Traducció per a l'altra persona: esperant",
    peerTranslatorAsleep: "Traducció per a l'altra persona: en pausa",
    relayed: 'Ruta: per TURN',
  },

  subtitles: {
    them: 'Ells',
    you: 'Vós',
    themEmpty: "L'altra persona encara no ha dit res",
    youEmpty: 'Digueu alguna cosa — les vostres paraules i la traducció apareixeran aquí',
  },

  rating: {
    accuracy: 'Precisió de la traducció',
    voice: 'Naturalitat de la veu',
    comfort: 'Comoditat de la conversa',
    send: 'Envia les valoracions',
    thanks: 'Gràcies — les valoracions s’han desat.',
  },

  errors: {
    no_credit: 'el compte d’OpenAI s’ha quedat sense crèdit — recarregueu-lo',
    rate_limited: 'el servei de traducció ens està limitant — torneu-ho a provar d’aquí a un minut',
    unconfigured: 'el servidor no té cap clau de traducció configurada',
    unreachable: 'el servidor no ha pogut arribar al servei de traducció',
    offline: 'no s’ha pogut arribar al servidor — comproveu la connexió',
    session_expired: 'la sessió de traducció ha caducat — reinicieu la traducció',
    room_closed: 'aquesta sala ja és tancada',
    bad_offer: 'el navegador ha generat una oferta de connexió no vàlida',
    provider_refused: (status: number) =>
      `el servei de traducció ha refusat la connexió (${status})`,
    unknown: 'no s’ha pogut iniciar la traducció',
  },

  banner: {
    providerUnconfigured:
      'El servidor no té configurada cap clau de proveïdor de traducció. Podeu crear una sala, però no es traduirà res.',
  },
};
